import type { LucideIcon } from 'lucide-react';
import { GitPullRequest, ShieldOff, Timer } from 'lucide-react';

export type CycleTimeSummaryMetricId =
  | 'prsMergedIntoQa'
  | 'prsMergedWithoutReview'
  | 'deployCadenceIntervals';

type CycleTimeSummary = Record<string, unknown> | null | undefined;

export type CycleTimeSummaryMetricConfig = {
  id: CycleTimeSummaryMetricId;
  title: string;
  icon: LucideIcon;
  selectId: string;
  selectLabel: string;
  emptyHint: string;
  getValue: (summary: CycleTimeSummary) => string | number;
};

export function formatPrsMergedWithoutReview(summary: CycleTimeSummary): string {
  const mergedCount = (summary?.mergedCount as number | undefined) ?? 0;
  const percentWithoutApproval = summary?.percentMergedWithoutApproval as number | null | undefined;
  if (mergedCount === 0) return 'N/A';
  if (percentWithoutApproval != null) return `${percentWithoutApproval}%`;
  return '—';
}

export const CYCLE_TIME_SUMMARY_METRICS: Record<CycleTimeSummaryMetricId, CycleTimeSummaryMetricConfig> =
  {
    prsMergedIntoQa: {
      id: 'prsMergedIntoQa',
      title: 'PRs merged into QA',
      icon: GitPullRequest,
      selectId: 'prs-merged-qa-window-select',
      selectLabel: 'PRs merged into QA window (days)',
      emptyHint: 'No PRs merged into the configured QA branch in this window.',
      getValue: (summary) =>
        (summary?.prCountToQa as number | undefined) ??
        (summary?.prCount as number | undefined) ??
        0,
    },
    prsMergedWithoutReview: {
      id: 'prsMergedWithoutReview',
      title: 'PRs merged without review',
      icon: ShieldOff,
      selectId: 'prs-without-review-window-select',
      selectLabel: 'PRs merged without review window (days)',
      emptyHint: 'No merged PRs in this window to calculate review compliance.',
      getValue: formatPrsMergedWithoutReview,
    },
    deployCadenceIntervals: {
      id: 'deployCadenceIntervals',
      title: 'Deploy cadence intervals',
      icon: Timer,
      selectId: 'deploy-cadence-window-select',
      selectLabel: 'Deploy cadence intervals window (days)',
      emptyHint: 'No deploy cadence intervals detected in this window.',
      getValue: (summary) => (summary?.deployCadenceIntervalCount as number | undefined) ?? 0,
    },
  };

export const CYCLE_TIME_SUMMARY_METRIC_ORDER: CycleTimeSummaryMetricId[] = [
  'prsMergedIntoQa',
  'prsMergedWithoutReview',
  'deployCadenceIntervals',
];
